/**
 * Grade to Course IDs Mapping for School Curriculum
 * Maps grade numbers to their corresponding course and variant IDs
 */
export const GRADE_TO_SCHOOL_CURRICULUM_IDS_MAPPING = {
  '2': {
    courseId: 'be373a1f-38fa-4fea-a934-cf4f8b8ba3e2',
    variantId: '5c1b5362-61ff-4cce-8881-a38ca520861d',
  },
  '3': {
    courseId: 'de996ffc-a913-43a8-9020-efeafb6f144d',
    variantId: '2799f1e2-cb38-47b3-b42a-c4ca3bc3196b',
  },
  '4': {
    courseId: '99fe2cf5-588e-4e34-8e84-a7d5a285e8cd',
    variantId: '8a3ada21-e21e-43db-bbb8-6e4e6cfa30ed',
  },
  '5': {
    courseId: '3cf8e255-7bf4-4ba9-a8fb-573b52d9e5f1',
    variantId: '5c2ff074-c489-438b-9bb3-8a68cf87d70e',
  },
  '6': {
    courseId: 'a949c55a-d923-493f-856f-6af89b1b7ee0',
    variantId: 'f24cdba3-771d-42a1-8245-ec21145c65aa',
  },
  '7': {
    courseId: '2b319bbb-c821-44f3-a6fc-d9a589b8111a',
    variantId: '1d7ce54a-1d24-40b0-8c4c-15f586a6c34c',
  },
  '8': {
    courseId: '0145359f-00b9-454f-9f45-8827401ba091',
    variantId: '84e469f6-e0d7-4c31-a0ab-94164e97a8fc',
  },
  '9': {
    courseId: 'e7ac7d9b-c855-4213-851a-96bff5ca39bf',
    variantId: 'db892ae8-c85d-45c0-bdfe-e9b2a398b52e',
  },
} as const;

/**
 * Array of all school curriculum variant IDs
 * Used to check if a user is already enrolled in school curriculum
 */
export const SCHOOL_CURRICULUM_VARIANT_IDS: string[] = Object.values(
  GRADE_TO_SCHOOL_CURRICULUM_IDS_MAPPING
).map(obj => obj.variantId);

/**
 * Available grades for school curriculum enrollment
 */
export const AVAILABLE_GRADES = Object.keys(
  GRADE_TO_SCHOOL_CURRICULUM_IDS_MAPPING
);

/**
 * Math Wizard course code suffix
 * Used to check if user is in Math Wizard course
 */
export const ELIGIBLE_COURSE_CODE_SUFFIX = '150';

/**
 * Grade to Course IDs Mapping for NAPLAN
 * Maps grade numbers to their corresponding variant IDs
 */
export const GRADE_TO_NAPLAN_IDS_MAPPING = {
  '3': {
    variantId: '23bf3107-4387-40f5-ba5d-de0fbddd18ef',
    gradeName: 'UKG-2',
  },
  '5': {
    variantId: '629e6d95-2f7d-458a-983b-ab63eeab0fba',
    gradeName: '3-4',
  },
  '7': {
    variantId: '4666abfc-8a79-440e-8e99-83350130c540',
    gradeName: '5-6',
  },
  '9': {
    variantId: '8fe3f7de-85d5-4772-91ec-d76b5fd5864f',
    gradeName: '7-8',
  },
} as const;

/**
 * Array of all NAPLAN variant IDs
 * Used to check if a user is already enrolled in NAPLAN
 */
export const NAPLAN_VARIANT_IDS: string[] = Object.values(
  GRADE_TO_NAPLAN_IDS_MAPPING
).map(obj => obj.variantId);

/**
 * ANZ Countries (Australia and New Zealand)
 * Used to check if user is from ANZ region for NAPLAN eligibility
 */
export const ANZ_COUNTRIES = ['AU', 'NZ'];

/**
 * Product types enum
 */
export enum ProductTypes {
  NAPLAN = 'Naplan',
  SCHOOL_CURRICULUM = 'School Curriculum',
  IMO = 'IMO',
}

/**
 * Available grades for NAPLAN enrollment
 */
export const AVAILABLE_NAPLAN_GRADES = Object.keys(GRADE_TO_NAPLAN_IDS_MAPPING);

/**
 * NAPLAN Product ID
 * Used for fetching batches for NAPLAN enrollment
 */
export const NAPLAN_PRODUCT_ID = 'dde337af-fe2e-4118-8eae-ab7bdc9349cc';

/**
 * NAPLAN Grade Mapping
 * Maps grade numbers to their corresponding grade names for API calls
 */
export const NAPLAN_GRADES = {
  '3': 'UKG-2',
  '5': '3-4',
  '7': '5-6',
  '9': '7-8',
} as const;

/**
 * IMO (International Math Olympiad) Product ID
 * Used to check if user is already enrolled in IMO
 */
export const IMO_PRODUCT_ID = '0801b505-2268-48f2-8b65-1093f98b7920';

/**
 * Grade to Variant ID Mapping for IMO
 * Maps grade numbers to their corresponding IMO variant IDs
 */
export const IMO_GRADE_TO_VARIANT_MAPPING = {
  '6': '446926ad-4457-4fac-9fa8-04638df51a23',
  '7': '666926ad-4457-4fac-9fa8-04638df51a73',
  '8': '776926ad-4457-4fac-9fa8-04638df51a99',
} as const;

/**
 * Array of all IMO variant IDs
 * Used to check if a user is already enrolled in IMO
 */
export const IMO_VARIANT_IDS: string[] = Object.values(
  IMO_GRADE_TO_VARIANT_MAPPING
);

/**
 * Math Wizard 150+ Product ID
 * Used to check if user is in the premium Math Wizard course
 */
export const BATCHES_150_PLUS_PRODUCT_ID =
  '7526fefb-f852-4799-9d27-b572f697439d';

/**
 * Eligible grades for IMO enrollment
 * Contains grade ranges that are eligible for IMO enrollment
 */
export const IMO_ENROLLMENT_GRADES = ['6-7', '8-8'] as const;

/**
 * Available grades for IMO enrollment (display format)
 * Derived from IMO_GRADE_TO_VARIANT_MAPPING keys
 */
export const AVAILABLE_IMO_GRADES = Object.keys(IMO_GRADE_TO_VARIANT_MAPPING);

/**
 * Mathlete 101 Configuration
 * Used for batch registration and course management
 * Configure via environment variables:
 * - VITE_MATHLETE_COURSE_ID
 * - VITE_MATHLETE_VARIANT_ID
 * - VITE_MATHLETE02_COURSE_ID
 * - VITE_MATHLETE02_VARIANT_ID
 */
export const MATHLETE_CONFIG = {
  courses: [
    {
      name: 'Mathlete 101',
      courseId: String(import.meta.env.VITE_MATHLETE_COURSE_ID ?? ''),
      variantId: String(import.meta.env.VITE_MATHLETE_VARIANT_ID ?? ''),
    },
    {
      name: 'Mathlete 101',
      courseId: String(import.meta.env.VITE_MATHLETE02_COURSE_ID ?? ''),
      variantId: String(import.meta.env.VITE_MATHLETE02_VARIANT_ID ?? ''),
    },
  ].filter(course => Boolean(course.courseId) && Boolean(course.variantId)),
  apiEndpoints: {
    getBatches: '/streams/v1/variant/batch',
    registerForBatch: '/user/v1/enroll_existing_profile',
  },
  queryKeys: {
    batches: 'mathlete-batches',
    registration: 'mathlete-batch-registration',
  },
  courseDescription: {
    totalSessions: '12',
    frequency: '3/week',
    duration: '55 min',
  },
} as const;

/**
 * Mathlete 101 Variant IDs (derived from config)
 * Used to check if user is enrolled in Mathlete 101 and needs batch registration
 */
export const MATHLETE_VARIANT_IDS: readonly string[] =
  MATHLETE_CONFIG.courses.map(course => String(course.variantId));

/**
 * API configuration for enrollment
 */
export const ENROLLMENT_API = {
  endpoints: {
    enrollToCourse: '/user/v1/enrol_to_course_from_student_dashboard',
  },
  queryKeys: {
    enrollToSchoolCurriculum: 'enroll-to-school-curriculum',
    enrollToNaplan: 'enroll-to-naplan',
    enrollToIMO: 'enroll-to-imo',
  },
} as const;
